import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import {
  Phone,
  Smartphone,
  Mail,
  Globe,
  IdCard,
  Download,
  Share2,
  Link2,
  Check,
  Building2,
} from "lucide-react";

const ORANGE = "#F39200";
const NAVY = "#0B1F3A";
const BLUE = "#0061B0";
const HEADING = "'Space Grotesk', 'Inter', sans-serif";

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <a
    href={href}
    className="flex items-center gap-3.5 group"
    data-testid={testid}
    {...(href && href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
  >
    <span
      className="flex items-center justify-center flex-shrink-0"
      style={{ width: 40, height: 40, borderRadius: 12, background: "#FFF4E3", color: ORANGE }}
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
    </span>
    <span className="flex flex-col leading-tight">
      <span style={{ fontSize: 10.5, letterSpacing: ".6px", color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
        {label}
      </span>
      <span style={{ fontSize: 14.5, color: NAVY, fontWeight: 600 }}>{value}</span>
    </span>
  </a>
);

export const BusinessCard = ({ person }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const cardUrl = `${window.location.origin}/vk/${person.slug}`;

  const buildVcf = () =>
    [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${person.lastName};${person.firstName};;;`,
      `FN:${person.firstName} ${person.lastName}`,
      `ORG:${person.company}`,
      `TITLE:${person.title}`,
      `TEL;TYPE=WORK,VOICE:${person.phone}`,
      `TEL;TYPE=CELL:${person.mobile}`,
      `EMAIL;TYPE=WORK:${person.email}`,
      `URL:https://${person.web}`,
      "END:VCARD",
    ].join("\r\n");

  const downloadVcf = () => {
    const blob = new Blob([buildVcf()], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${person.slug}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const a = document.createElement("a");
      a.download = `visitenkarte-${person.slug}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${person.firstName} ${person.lastName} — ${person.company}`,
          text: `Digitale Visitenkarte von ${person.firstName} ${person.lastName}`,
          url: cardUrl,
        });
      } catch (e) {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full" data-testid={`business-card-${person.slug}`}>
      {/* ===== Card (captured for PNG) ===== */}
      <div
        ref={cardRef}
        style={{
          width: 380,
          maxWidth: "100%",
          background: "#fff",
          borderRadius: 28,
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 30px 60px -22px rgba(11,31,58,.45)",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {/* Navy header band — Logo zentriert & größer */}
        <div style={{ position: "relative", height: 172, background: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: "radial-gradient(rgba(255,255,255,.12) 1.4px, transparent 1.4px)",
              backgroundSize: "18px 18px",
            }}
          />
          <img
            src="/assets/rnetwork-logo.webp"
            alt="R-NetWork"
            crossOrigin="anonymous"
            style={{ height: 42, position: "relative", filter: "brightness(0) invert(1)" }}
          />
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 6, background: ORANGE }} />
        </div>

        {/* Avatar */}
        <div
          style={{
            position: "absolute",
            left: 30,
            top: 120,
            width: 104,
            height: 104,
            borderRadius: 26,
            background: `linear-gradient(135deg, ${BLUE}, #003e76)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: HEADING,
            fontWeight: 700,
            fontSize: 36,
            border: "5px solid #fff",
            boxShadow: "0 14px 30px -8px rgba(0,97,176,.5)",
          }}
        >
          {person.initials}
        </div>

        {/* Body */}
        <div style={{ padding: "60px 30px 28px" }}>
          <h2 style={{ fontFamily: HEADING, fontSize: 27, color: NAVY, fontWeight: 700, lineHeight: 1.1 }}>
            {person.firstName} {person.lastName}
          </h2>
          <div style={{ color: ORANGE, fontWeight: 700, fontSize: 14.5, marginTop: 4 }}>{person.title}</div>
          <div style={{ color: "#475569", fontSize: 13, marginTop: 4, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            <Building2 className="w-[14px] h-[14px]" /> {person.company} · {person.tagline}
          </div>

          <div className="mt-6 flex flex-col gap-3.5">
            <ContactRow icon={Phone} label="Telefon" value={person.phone} href={`tel:${person.phone.replace(/\s/g, "")}`} testid="card-phone" />
            <ContactRow icon={Smartphone} label="Mobil" value={person.mobile} href={`tel:${person.mobile.replace(/\s/g, "")}`} testid="card-mobile" />
            <ContactRow icon={Mail} label="E-Mail" value={person.email} href={`mailto:${person.email}`} testid="card-email" />
            <ContactRow icon={Globe} label="Web" value={person.web} href={`https://${person.web}`} testid="card-web" />
          </div>

          {/* Footer: QR + save */}
          <div
            className="flex items-center gap-4 mt-6 pt-5"
            style={{ borderTop: "1px solid #eef2f7" }}
          >
            <div style={{ width: 76, height: 76, borderRadius: 14, border: "1px solid #eef2f7", padding: 6, flexShrink: 0 }}>
              <QRCodeCanvas value={cardUrl} size={64} fgColor={NAVY} bgColor="#ffffff" level="M" />
            </div>
            <button
              onClick={downloadVcf}
              data-testid={`save-contact-${person.slug}`}
              className="flex-1 flex items-center justify-center gap-2 text-white"
              style={{
                background: NAVY,
                fontFamily: HEADING,
                fontWeight: 700,
                border: "none",
                padding: "14px",
                borderRadius: 14,
                fontSize: 13.5,
                cursor: "pointer",
              }}
            >
              <IdCard className="w-[18px] h-[18px]" /> Kontakt speichern
            </button>
          </div>
        </div>
      </div>

      {/* ===== Utility actions (not captured) ===== */}
      <div className="flex items-center gap-2 flex-wrap justify-center" style={{ width: 380, maxWidth: "100%" }}>
        <button
          onClick={share}
          data-testid={`share-${person.slug}`}
          className="flex items-center gap-2 text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
          style={{ background: ORANGE, color: "#1a1206" }}
        >
          <Share2 className="w-4 h-4" /> Teilen
        </button>
        <button
          onClick={copyLink}
          data-testid={`copy-link-${person.slug}`}
          className="flex items-center gap-2 text-sm font-medium rounded-xl px-4 py-2.5 border transition-colors"
          style={{ borderColor: "#dbe3ee", color: NAVY }}
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
          {copied ? "Kopiert!" : "Link kopieren"}
        </button>
        <button
          onClick={downloadPng}
          disabled={busy}
          data-testid={`download-png-${person.slug}`}
          className="flex items-center gap-2 text-sm font-medium rounded-xl px-4 py-2.5 border transition-colors disabled:opacity-60"
          style={{ borderColor: "#dbe3ee", color: NAVY }}
        >
          <Download className="w-4 h-4" /> {busy ? "Erstelle…" : "Als Bild (PNG)"}
        </button>
      </div>
    </div>
  );
};

export default BusinessCard;
